import { Combobox, Field, Label, Transition } from "@headlessui/react";
import clsx from "clsx";
import { useState } from "react";

import { Icon } from "../Icon";
import Tooltip from "../Tooltip";

type OptionsType = {
  available?: boolean;
  label: string;
  value: string;
};

export default function SearchableSelectBox({
  label,
  options,
  placeholder,
  disabled = false,
  onChange,
  selected,
  tooltipText,
  onClose,
  className,
  dropUp = false,
}: {
  className?: string;
  disabled?: boolean;
  dropUp?: boolean;
  label?: string;
  onChange: (value: string) => void;
  onClose?: () => void;
  options: OptionsType[];
  placeholder: string;
  selected?: string;
  tooltipText?: string;
}) {
  const [query, setQuery] = useState("");

  const filteredOptions =
    query === ""
      ? options
      : options.filter((option) => {
          return option.label.toLowerCase().includes(query.toLowerCase());
        });

  return (
    <Field>
      {label && (
        <div className="flex mb-1 gap-2 items-center">
          <Label
            className={clsx(
              "block text-xs font-medium leading-5 tracking-wide",
              disabled ? "text-gray-400" : "text-gray-700",
            )}
          >
            {label}
          </Label>
          {tooltipText && <Tooltip text={tooltipText} />}
        </div>
      )}
      <Combobox
        value={selected}
        defaultValue=""
        onChange={(value) => {
          if (value !== null) {
            onChange(value);
          }
        }}
        onClose={() => setQuery("")}
        immediate={true}
        disabled={disabled}
      >
        {({ open }) => (
          <div className="relative w-full">
            <div>
              <Combobox.Input
                value={
                  query
                    ? query
                    : options.find((opt) => opt.value === selected)?.label
                }
                onChange={(event) => setQuery(event.target.value)}
                defaultValue=""
                className={clsx(
                  "tw-form-field-style placeholder:text-gray-900 text-xs md:text-sm",
                  className,
                )}
                placeholder={placeholder}
              />
              {onClose && selected && (
                <span
                  onClick={onClose}
                  className="absolute right-2 top-2 cursor-pointer hover:text-gray-700 text-gray-400"
                >
                  <Icon name="xMark" />
                </span>
              )}
            </div>
            <Transition
              show={open && filteredOptions.length > 0}
              as="div"
              enter="transition duration-100 ease-out"
              enterFrom="transform scale-95 opacity-0"
              enterTo="transform scale-100 opacity-100"
              leave="transition ease-in duration-100"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
              className="w-full"
            >
              <Combobox.Options
                className={clsx(
                  "absolute z-[60] !max-h-56 w-full overflow-auto rounded-md bg-white py-1 text-xs md:text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none",
                  dropUp ? "bottom-full mb-1" : "top-full mt-1",
                )}
                static
              >
                {filteredOptions.map((option, idx) => (
                  <Combobox.Option
                    key={`option-${idx}`}
                    value={option.value}
                    className={() =>
                      clsx(
                        "relative cursor-default select-none",
                        "group flex justify-between cursor-default items-center gap-2 px-3 py-3 md:py-1 select-none leading-5 data-[focus]:bg-gray-100 data-[focus]:text-black",
                      )
                    }
                  >
                    <span className="text-xs md:text-sm">{option.label}</span>

                    {selected === option.value ? (
                      <Icon name="circleCheck" className="h-3 w-3" />
                    ) : null}
                  </Combobox.Option>
                ))}
              </Combobox.Options>
            </Transition>
          </div>
        )}
      </Combobox>
    </Field>
  );
}
